import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Schedule from '@mui/icons-material/Schedule';
import { Box, Stack } from '@mui/material';
import { getMissingApprovals } from 'lib/api/approvals';
import {
  type ApprovalType,
  COMITEE_TYPES,
  type RoleApproval,
} from 'lib/api/approvals/types';
import {
  CustomButton,
  ErrorState,
  IconTitleCard,
  IconTitleCardSkeleton,
  MobileBody2,
  MobileSubtitle,
  MobileTitle,
} from 'lib/mobileDesignSystem';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

type RoleStatusKey = 'success' | 'warning' | 'error';

const ROLE_STATUS_CONFIG: Record<
  RoleStatusKey,
  {
    iconColor: string;
    Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>;
  }
> = {
  success: { iconColor: 'success', Icon: CheckCircle },
  warning: { iconColor: 'warning', Icon: Schedule },
  error: { iconColor: 'error', Icon: Cancel },
};

function getRoleStatusKey(role: RoleApproval): RoleStatusKey {
  if (role.rejected.length > 0) return 'error';
  if (role.approved.length === role.requiredApprovals) return 'success';
  return 'warning';
}

const APPROVALS_SKELETON_COUNT = 2;

export interface ApprovalStateUserRole {
  id: number;
  name: string;
}

export interface SharedApprovalsDrawerProps {
  approvalType: ApprovalType;
  entityId: number | null;
  requestId: string | null;
  approvalPath: string;
  buildApprovalState: (
    type: 'approve' | 'reject',
    userRoles: ApprovalStateUserRole[],
  ) => Record<string, unknown>;
}

const SharedApprovalsDrawer: React.FC<SharedApprovalsDrawerProps> = ({
  approvalType,
  entityId,
  requestId,
  approvalPath,
  buildApprovalState,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: missingApprovalsData,
    isLoading: approvalsLoading,
    isError: approvalsError,
  } = getMissingApprovals(
    dispatch,
    approvalType,
    entityId ?? 0,
    COMITEE_TYPES.RISK,
  );

  const title = missingApprovalsData?.comiteeName ?? 'Aprobaciones';
  const roles = missingApprovalsData?.roles ?? [];

  const userRoles = useMemo(
    () =>
      roles
        .filter((r) => r.userCanApprove)
        .map((r) => ({ id: r.roleId, name: r.roleName })),
    [roles],
  );

  const canApprove = userRoles.length > 0;
  const hasRequestId = requestId != null && requestId !== '';
  const isComplete =
    missingApprovalsData?.isFullyApproved === true ||
    missingApprovalsData?.isRejected === true;

  const handleApprove = () => {
    if (!canApprove || !hasRequestId || approvalPath === '') return;
    navigate(approvalPath, {
      state: buildApprovalState('approve', userRoles),
    });
  };

  const handleReject = () => {
    if (!canApprove || !hasRequestId || approvalPath === '') return;
    navigate(approvalPath, {
      state: buildApprovalState('reject', userRoles),
    });
  };

  if (approvalsError) {
    return (
      <ErrorState message="Ocurrió un error al cargar las aprobaciones." />
    );
  }

  if (approvalsLoading) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <MobileTitle gutterBottom>Cómite solicitudes</MobileTitle>
        </Box>
        <Stack spacing={2} sx={{ pt: 2 }}>
          {Array.from({ length: APPROVALS_SKELETON_COUNT }, (_, i) => (
            <IconTitleCardSkeleton key={`skeleton-${i}`} />
          ))}
        </Stack>
      </>
    );
  }

  return (
    <Stack sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1,
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <MobileTitle gutterBottom>{title}</MobileTitle>
        <MobileBody2>Aprobaciones faltantes:</MobileBody2>
      </Box>
      <Stack spacing={2} sx={{ pt: 2, flex: 1, overflow: 'auto' }}>
        {roles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <MobileBody2 color="text.secondary">
              No hay aprobaciones pendientes.
            </MobileBody2>
          </Box>
        ) : (
          <Stack spacing={0}>
            {roles.map((role: RoleApproval) => {
              const statusKey = getRoleStatusKey(role);
              const { iconColor, Icon } = ROLE_STATUS_CONFIG[statusKey];
              return (
                <Box
                  key={role.roleId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <IconTitleCard
                      icon={<Icon fontSize="small" />}
                      IconColor={iconColor}
                      title={role.roleName}
                      description="Rol de aprobación"
                      sx={{ px: 1.5, py: 1.25 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      pr: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <MobileSubtitle
                      component="span"
                      sx={{
                        color: 'text.primary',
                        fontSize: '1rem',
                        fontWeight: 'inherit',
                      }}
                    >
                      {`${role.approved.length} / ${role.requiredApprovals}`}
                    </MobileSubtitle>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      {canApprove && !isComplete && (
        <Box sx={{ pt: 2, pb: 0, mt: 'auto' }}>
          <Stack direction="row" spacing={2}>
            <CustomButton
              variant="error"
              sx={{ flex: 1 }}
              onClick={handleReject}
              disabled={!hasRequestId}
            >
              Rechazar
            </CustomButton>
            <CustomButton
              variant="success"
              sx={{ flex: 1 }}
              onClick={handleApprove}
              disabled={!hasRequestId}
            >
              Aprobar
            </CustomButton>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default SharedApprovalsDrawer;
